import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import PageHelpLink from "../../../Components/Common/PageHelpLink";
import { getLoggedinUser } from "../../../helpers/api_helper";
import { helpUrlForRoute } from "../../../constants/helpRouteMap";
import {
  fetchSitePixel,
  updateSitePixel,
  type SitePixelConfig,
} from "../../../helpers/site_pixel_helper";

/**
 * Verifica se o tenant tem tracking hostname configurado (aviso no pixel).
 */
function sessionHasTrackingHostname(): boolean {
  const session = getLoggedinUser();
  const host = session?.tenant?.tracking_hostname;
  return typeof host === "string" && host.trim() !== "";
}

/**
 * Pixel do site (F2) — opt-in; snippet JS para taggar visitantes (DOC 11 § 9).
 * Por quê: Carla cola no site; identify(email) / track(nome) aplicam tags.
 */
const SitePixelPage = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || "pt-BR";

  document.title = t("integrations.sitePixel.documentTitle");

  const helpHref = helpUrlForRoute("/integrations/site-pixel", locale);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [config, setConfig] = useState<SitePixelConfig | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [pageviewTags, setPageviewTags] = useState("");
  const [identifyTags, setIdentifyTags] = useState("");
  const [purgeDays, setPurgeDays] = useState(90);
  const [eventsMap, setEventsMap] = useState<
    Array<{ event_name: string; tag_names: string }>
  >([]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await fetchSitePixel();
      if (!res?.success || !res.data) {
        setLoadError(t("integrations.sitePixel.loadError"));
        return;
      }
      const cfg = res.data;
      setConfig(cfg);
      setEnabled(cfg.enabled);
      setPageviewTags(cfg.pageview_tag_names || "");
      setIdentifyTags(cfg.identify_tag_names || "");
      setPurgeDays(cfg.purge_days || 90);
      setEventsMap(cfg.events_map || []);
    } catch (e: any) {
      setLoadError(
        e?.response?.data?.error?.message ||
          e?.message ||
          t("integrations.sitePixel.loadError")
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const res = await updateSitePixel({
        enabled,
        purge_days: purgeDays,
        pageview_tag_names: pageviewTags.trim() || null,
        identify_tag_names: identifyTags.trim() || null,
        events_map: eventsMap.filter((e) => e.event_name.trim()),
      });
      if (!res?.success || !res.data) {
        setSaveError(t("integrations.sitePixel.saveError"));
        return;
      }
      setConfig(res.data);
      toast.success(
        res.message || t("integrations.sitePixel.saveSuccess"),
        { autoClose: 3000 }
      );
    } catch (e: any) {
      const msg =
        e?.response?.data?.error?.message ||
        e?.message ||
        t("integrations.sitePixel.saveError");
      setSaveError(msg);
      toast.error(msg, { autoClose: 5000 });
    } finally {
      setSaving(false);
    }
  };

  const copySnippet = () => {
    if (!config?.snippet) {
      return;
    }
    navigator.clipboard
      .writeText(config.snippet)
      .then(() => {
        toast.success(t("integrations.sitePixel.snippetCopied"), {
          autoClose: 2000,
        });
      })
      .catch(() => {
        toast.error(t("integrations.sitePixel.saveError"), {
          autoClose: 3000,
        });
      });
  };

  const addEventMap = () => {
    setEventsMap([...eventsMap, { event_name: "", tag_names: "" }]);
  };

  const removeEventMap = (index: number) => {
    setEventsMap(eventsMap.filter((_, i) => i !== index));
  };

  const updateEventMap = (
    index: number,
    field: "event_name" | "tag_names",
    value: string
  ) => {
    const updated = [...eventsMap];
    updated[index][field] = value;
    setEventsMap(updated);
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("integrations.sitePixel.title")}
          pageTitle={t("menu.integrations")}
        />
        <div className="d-flex justify-content-between align-items-start mb-3 gap-2 flex-wrap">
          <p className="text-muted mb-0">
            {t("integrations.sitePixel.subtitle")}
          </p>
          <PageHelpLink
            href={helpHref}
            label={t("integrations.sitePixel.help")}
          />
        </div>

        <ToastContainer closeButton={false} limit={3} />

        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <div className="mt-2 text-muted">
              {t("integrations.sitePixel.loading")}
            </div>
          </div>
        ) : loadError ? (
          <Alert color="danger">
            {loadError}
            <div className="mt-2">
              <Button
                color="soft-danger"
                size="sm"
                onClick={() => void load()}
              >
                {t("settings.sending.retry")}
              </Button>
            </div>
          </Alert>
        ) : (
          <Row>
            <Col lg={8}>
              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">
                    {t("integrations.sitePixel.title")}
                  </h5>
                </CardHeader>
                <CardBody>
                  {saveError && (
                    <Alert color="danger" className="mb-3">
                      {saveError}
                    </Alert>
                  )}

                  {!enabled && (
                    <Alert color="info" className="mb-3">
                      <h6 className="alert-heading mb-2">
                        {t("integrations.sitePixel.emptyTitle")}
                      </h6>
                      <p className="mb-2">
                        {t("integrations.sitePixel.emptyBody")}
                      </p>
                      <ol className="mb-2 ps-3">
                        <li>{t("integrations.sitePixel.emptyStep1")}</li>
                        <li>{t("integrations.sitePixel.emptyStep2")}</li>
                        <li>{t("integrations.sitePixel.emptyStep3")}</li>
                      </ol>
                      <Button
                        color="primary"
                        size="sm"
                        onClick={() => setEnabled(true)}
                      >
                        {t("integrations.sitePixel.emptyCta")}
                      </Button>
                    </Alert>
                  )}

                  {enabled && config?.snippet && !sessionHasTrackingHostname() && (
                    <Alert color="warning" className="mb-3">
                      <div className="d-flex align-items-start gap-2">
                        <i className="ri-alert-line fs-18 mt-1" />
                        <div>
                          <div className="fw-medium mb-1">
                            {t("integrations.sitePixel.trackingWarningTitle")}
                          </div>
                          <div className="text-muted small">
                            {t("integrations.sitePixel.trackingWarningBody")}
                          </div>
                        </div>
                      </div>
                    </Alert>
                  )}

                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      void handleSave();
                    }}
                  >
                    <div className="mb-3">
                      <div className="form-check form-switch">
                        <Input
                          id="pixel-enabled"
                          type="checkbox"
                          className="form-check-input"
                          checked={enabled}
                          onChange={(e) => setEnabled(e.target.checked)}
                        />
                        <Label
                          htmlFor="pixel-enabled"
                          className="form-check-label"
                        >
                          {t("integrations.sitePixel.enabled")}
                        </Label>
                      </div>
                      <div className="form-text">
                        {t("integrations.sitePixel.enabledHint")}
                      </div>
                    </div>

                    {enabled && config?.snippet && (
                      <div className="mb-3">
                        <Label>{t("integrations.sitePixel.snippet")}</Label>
                        <div className="position-relative">
                          <pre
                            className="bg-light border rounded p-3"
                            style={{
                              fontSize: "0.85rem",
                              maxHeight: "200px",
                              overflow: "auto",
                            }}
                          >
                            <code>{config.snippet}</code>
                          </pre>
                          <Button
                            color="primary"
                            size="sm"
                            className="position-absolute top-0 end-0 m-2"
                            onClick={copySnippet}
                          >
                            {t("integrations.sitePixel.copySnippet")}
                          </Button>
                        </div>
                        <div className="form-text">
                          {t("integrations.sitePixel.snippetHint")}
                        </div>
                      </div>
                    )}

                    <div className="mb-3">
                      <Label htmlFor="pageview-tags">
                        {t("integrations.sitePixel.pageviewTags")}
                      </Label>
                      <Input
                        id="pageview-tags"
                        type="text"
                        value={pageviewTags}
                        onChange={(e) => setPageviewTags(e.target.value)}
                        placeholder="visitante, site"
                      />
                      <div className="form-text">
                        {t("integrations.sitePixel.pageviewTagsHint")}
                      </div>
                    </div>

                    <div className="mb-3">
                      <Label htmlFor="identify-tags">
                        {t("integrations.sitePixel.identifyTags")}
                      </Label>
                      <Input
                        id="identify-tags"
                        type="text"
                        value={identifyTags}
                        onChange={(e) => setIdentifyTags(e.target.value)}
                        placeholder="identificado"
                      />
                      <div className="form-text">
                        {t("integrations.sitePixel.identifyTagsHint")}
                      </div>
                    </div>

                    <div className="mb-3">
                      <Label htmlFor="purge-days">
                        {t("integrations.sitePixel.purgeDays")}
                      </Label>
                      <Input
                        id="purge-days"
                        type="number"
                        min={7}
                        max={365}
                        value={purgeDays}
                        onChange={(e) =>
                          setPurgeDays(
                            Math.min(365, Math.max(7, Number(e.target.value) || 90))
                          )
                        }
                      />
                      <div className="form-text">
                        {t("integrations.sitePixel.purgeDaysHint")}
                      </div>
                    </div>

                    <div className="mb-3">
                      <Label>{t("integrations.sitePixel.eventsMap")}</Label>
                      <div className="form-text mb-2">
                        {t("integrations.sitePixel.eventsMapHint")}
                      </div>
                      {eventsMap.map((event, index) => (
                        <Row key={index} className="g-2 mb-2">
                          <Col md={5}>
                            <Input
                              type="text"
                              value={event.event_name}
                              onChange={(e) =>
                                updateEventMap(
                                  index,
                                  "event_name",
                                  e.target.value
                                )
                              }
                              placeholder={t(
                                "integrations.sitePixel.eventName"
                              )}
                            />
                          </Col>
                          <Col md={5}>
                            <Input
                              type="text"
                              value={event.tag_names}
                              onChange={(e) =>
                                updateEventMap(
                                  index,
                                  "tag_names",
                                  e.target.value
                                )
                              }
                              placeholder={t(
                                "integrations.sitePixel.eventTags"
                              )}
                            />
                          </Col>
                          <Col md={2}>
                            <Button
                              color="soft-danger"
                              size="sm"
                              onClick={() => removeEventMap(index)}
                            >
                              {t("integrations.sitePixel.removeEvent")}
                            </Button>
                          </Col>
                        </Row>
                      ))}
                      <Button
                        color="soft-primary"
                        size="sm"
                        onClick={addEventMap}
                      >
                        {t("integrations.sitePixel.addEvent")}
                      </Button>
                    </div>

                    {enabled && config?.snippet && (
                      <div className="mb-3">
                        <Label>{t("integrations.sitePixel.exampleUsage")}</Label>
                        <div className="form-text mb-2">
                          {t("integrations.sitePixel.exampleUsageHint")}
                        </div>
                        <pre
                          className="bg-light border rounded p-3"
                          style={{
                            fontSize: "0.85rem",
                            maxHeight: "200px",
                            overflow: "auto",
                          }}
                        >
                          <code>{`// Identificar visitante (envia e-mail)
window.brenvioPixel?.identify('maria@exemplo.com');

// Aplicar tag manualmente
window.brenvioPixel?.track('clicou_cta_promo');

// Aplicar múltiplas tags
window.brenvioPixel?.track('comprou', { tags: ['cliente', 'vip'] });`}</code>
                        </pre>
                      </div>
                    )}

                    <Button color="primary" type="submit" disabled={saving}>
                      {saving ? (
                        <>
                          <Spinner size="sm" className="me-2">
                            …
                          </Spinner>
                          {t("integrations.sitePixel.saving")}
                        </>
                      ) : (
                        t("integrations.sitePixel.save")
                      )}
                    </Button>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </Container>
    </div>
  );
};

export default SitePixelPage;
